/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Features section for AI TikTok Video Generator - alternating image-text layout
 */
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

interface FeatureItem {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

const features: FeatureItem[] = [
  {
    title: 'Turn Prompts into Viral TikTok Videos',
    description:
      "Transform simple ideas into high-energy, TikTok-ready videos in seconds. Just enter a short script, prompt, or product link — FalcoCut automatically generates scenes, captions, music, pacing, and transitions that match TikTok's fast, native style. No timelines. No manual editing. No templates to tweak. Perfect for creators, marketers, and brands who want to publish more content, faster, without sacrificing quality.",
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/prompts-to-video.webp',
    imageAlt: 'Turn prompts into viral TikTok videos with AI',
  },
  {
    title: 'Auto-Generate Trend-Style Clips',
    description:
      "Stay on trend without chasing editing hacks or copying viral videos frame by frame. FalcoCut studies popular TikTok formats and applies trending cuts, text pop-ups, sound effects, and punchy pacing automatically. Your videos don't just look edited — they look native. Each clip is optimized to match what's already performing in users' feeds.",
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/trend-style-clips.webp',
    imageAlt: 'Auto-generate trend-style TikTok clips with AI',
  },
  {
    title: 'Product-to-Video for UGC & Ads',
    description:
      'Paste a product link or upload images and videos — FalcoCut instantly turns them into polished, UGC-style videos designed for TikTok ads and organic posts. Generate product demos, explainer scenes, and creator-style ads that feel authentic, not overly produced. Ideal for brands, dropshippers, and performance marketers who need high-volume ad creatives.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/product-to-video.webp',
    imageAlt: 'Product-to-video for UGC and TikTok ads',
  },
  {
    title: 'Realistic Voiceovers & Auto-Captions',
    description:
      'Bring your story to life with natural-sounding voiceovers and perfectly timed captions — built for short-form platforms like TikTok. FalcoCut generates expressive, human-like voices with pacing, tone, and emphasis that feel native to social feeds. Captions are automatically synced to speech and styled to keep viewers watching — even with the sound off.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/voiceovers-captions.webp',
    imageAlt: 'Realistic voiceovers and auto-captions for TikTok',
  },
  {
    title: 'Long Video to TikTok Shorts',
    description:
      "Turn one long video into 5-20 attention-grabbing short clips — automatically. FalcoCut analyzes your content, identifies the most engaging moments, removes silence, tightens pacing, and reformats everything for vertical viewing. Whether it's a podcast, webinar, or talking-head video, repurpose long-form content into scroll-stopping TikTok Shorts without manual editing.",
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/long-to-shorts.webp',
    imageAlt: 'Convert long videos to TikTok Shorts',
  },
  {
    title: 'Publish-Ready, In Minutes',
    description:
      'Once your video is polished, export instantly for TikTok, Reels, and Shorts. No extra formatting, resizing, or last-minute tweaks required. FalcoCut streamlines your entire workflow — from idea to publish — inside one platform. Create, edit, localize, and export in minutes, so you can spend less time editing and more time posting, testing, and scaling what works.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/publish-ready.webp',
    imageAlt: 'Publish-ready TikTok videos in minutes',
  },
];

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

interface FeatureCardProps {
  feature: FeatureItem;
  isReversed: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ feature, isReversed }) => {
  return (
    <div
      className={`flex flex-col ${isReversed ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-8 pad:gap-10 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden bg-gray-100">
          <Image
            src={feature.imageUrl}
            alt={feature.imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full">
        <h3 className="text-xl pad:text-2xl font-bold text-gray-900 mb-4">
          {feature.title}
        </h3>
        <p className="text-sm pad:text-base text-gray-600 leading-relaxed mb-6">
          {feature.description}
        </p>
        <Link
          href="/ai-video-generator"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
          style={{ background: '#745CFF' }}
        >
          <span>Create TikTok Videos</span>
          <ArrowRightIcon />
        </Link>
      </div>
    </div>
  );
};

const FeaturesSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Header */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
          Powerful AI TikTok Video Generation
        </h2>
        <p className="text-sm pad:text-base text-gray-600 text-center max-w-4xl mx-auto mb-12 pad:mb-16">
          FalcoCut brings everything you need for TikTok creation into one simple
          platform. Turn text or images into short videos, add clear subtitles, and
          generate multi-language voiceovers without switching tools. Every step works
          together, helping you create authentic, TikTok-ready content in minutes.
        </p>

        {/* Features List */}
        <div className="space-y-16 pad:space-y-20">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              isReversed={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
